import { TFunction } from 'i18next';
import { DateCell, DateTimeCell } from '@components-clean//core/grid-fe/custom-cells';
import { CustomCells } from '@components-clean/core/grid/custom-cell';
import { statusChips } from '@components-clean/core/grid/custom-cell/chip-constants';
import { GridColumnProps } from '@components-clean/core/grid/grid.interface';
import { ReportModules } from '@interfaces/report-templates.interface';
import Constants from '@utils/constants';

export const GENERATED_REPORTS_GRID_COLUMNS = (
    t: TFunction,
    customCells: CustomCells,
    module: ReportModules,
    isGenerationHistory?: boolean,
    includeNameAndVersion?: boolean,
    includeComparisonDataset?: boolean,
): GridColumnProps[] => {
    const { TooltipCell, ChipCell } = customCells;
    const isCM = module === Constants.Modules.CM2;
    const showNameAndVer = isGenerationHistory ? true : !!includeNameAndVersion;

    const templateCol: GridColumnProps = {
        field: 'templateName',
        title: t('reports:templateName'),
        minWidth: 450,
        lockedByDefault: true,
        id: 'report-template-name',
        cells: {
            data: TooltipCell({
                isCsv: false,
            }),
        },
    };

    const nameAndVersionCols: GridColumnProps[] = [
        {
            field: 'fullName',
            title: t('reports:reportName'),
            minWidth: 270,
            lockedByDefault: !isGenerationHistory && showNameAndVer,
            id: 'report-name',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
            showByDefault: showNameAndVer,
        },
        {
            field: 'reportVersion',
            lockedByDefault: !isGenerationHistory && showNameAndVer,
            title: t('reports:reportVersion'),
            minWidth: 130,
            id: 'report-version',
            showByDefault: showNameAndVer,
        },
    ];
    const datasetsCols: GridColumnProps[] = [
        {
            field: 'dataset',
            title: t('cm:dataset'),
            minWidth: isGenerationHistory ? 156 : 200,
            id: 'report-dataset',
            cells: {
                data: DateCell,
            },
            filter: 'date',
            cellName: 'DateCell',
        },
        {
            field: 'comparisonDataset',
            title: t('cm:comparisonDataset'),
            minWidth: isGenerationHistory ? 156 : 200,
            id: 'report-comparison-dataset',
            cells: {
                data: DateCell,
            },
            filter: 'date',
            showByDefault: isGenerationHistory ? true : !!includeComparisonDataset,
            cellName: 'DateCell',
        },
    ];

    const generatedDateAndByCols: GridColumnProps[] = [
        {
            field: 'createdDate',
            title: t('reports:generatedDate'),
            id: 'report-generated-date',
            minWidth: isGenerationHistory ? 176 : 200,
            cells: {
                data: DateTimeCell('DD-MMM-yyyy HH:mm:ss UTC'),
            },
            filter: 'date',
        },
        {
            field: 'createdByName',
            title: t('reports:generatedBy'),
            id: 'report-generated-by',
            minWidth: isGenerationHistory ? 176 : 200,
        },
    ];

    const statusAndCommentsCols: GridColumnProps[] = [
        {
            field: '_status',
            title: t('cm:status'),
            minWidth: isGenerationHistory ? 156 : 200,
            id: 'report-status',
            cells: {
                data: ChipCell(statusChips, { width: 95 }),
            },
        },
        {
            field: 'comments',
            title: t('cm:comments'),
            minWidth: isGenerationHistory ? 570 : 690,
            id: 'risk-review-comments',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
    ];

    const recordIdCol: GridColumnProps = {
        field: '_itemId',
        title: t('id'),
        minWidth: 85,
        id: 'risk-review-id',
        showByDefault: false,
    };

    if (isGenerationHistory) {
        return [
            templateCol,
            ...nameAndVersionCols,
            ...(isCM ? datasetsCols : []),
            ...statusAndCommentsCols,
            ...generatedDateAndByCols,
            recordIdCol,
        ];
    }

    return [
        ...nameAndVersionCols,
        ...(isCM ? datasetsCols : []),
        ...generatedDateAndByCols,
        ...statusAndCommentsCols,
        recordIdCol,
    ];
};
